import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

import {
  Button,
  Flex,
  H3,
  P,
  Pagination,
  SelectMenu,
  Tooltip,
} from '@decisiv/ui-components';
import { t, Trans } from '@lingui/macro';
import iconix from '@decisiv/iconix/lib/components';
import Table from '@decisiv/ui-components/lib/components/Table';
import Tag from '@decisiv/ui-components/lib/components/Tag';
import { Link } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { reverse, sortBy } from 'lodash';
import Label from '../../components/Label';
import { dealerTypeOptions, oemOptions } from '../../utils/constants';
import WrapperWithLoading from '../../components/WrapperWithLoading';
import NoLocationsFound from './NoLocationsFound';
import ConfiguredIntegrationsApi from '../../api/configured_integrations';
import {
  FilterTextField,
  HelpInputMessage,
  HelpInputText,
  Separator,
} from './style';
import authorizeOn from '../../utils/resourceAuthorizer';

export default function LocationsResult({
  integrationId,
  setVisibleModal,
  setVisibleExportModal,
  inSharedSetting = false,
}) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [totalLocations, setTotalLocations] = useState(0);
  // Only ever read when a column is returned to 'unsorted'. Held in a ref so
  // refetching does not re-render, and so onSort can stay reference-stable.
  const initialLocationsRef = useRef([]);
  const [totalPages, setTotalPages] = useState(0);
  const [filterSearch, setFilterSearch] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [filterOem, setFilterOem] = useState(['all']);
  const [filterDealerType, setFilterDealerType] = useState(['all']);

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const response =
        await ConfiguredIntegrationsApi.fetchConfiguredIntegrations(
          integrationId,
          currentPage,
          itemsPerPage,
          {
            search: filterSearch,
            oem: filterOem,
            accountType: filterDealerType,
          },
        );

      setLocations(response.data);
      initialLocationsRef.current = response.data; // Store original order
      setTotalLocations(response.totalRecords);
      setTotalPages(Math.ceil(response.totalRecords / itemsPerPage));
    } catch (error) {
      console.error('Error fetching locations: ', error);
      setLocations([]);
      initialLocationsRef.current = [];
    } finally {
      setLoading(false);
    }
  }, [
    integrationId,
    currentPage,
    itemsPerPage,
    filterSearch,
    filterOem,
    filterDealerType,
  ]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const pageChangeHandler = (newPageNumber) => {
    setCurrentPage(newPageNumber);
  };

  // Sorts through the functional form and reads the original order from a ref,
  // so this stays stable across renders — otherwise every column's `sortBy`
  // closure would be new and the whole column set would be rebuilt on each sort.
  const onSort = useCallback(
    (columnName) => (sortingType) => {
      const valueOf = (item) => {
        if (columnName.includes('attributes.')) {
          return item.attributes[columnName.replace('attributes.', '')];
        }
        return item[columnName];
      };

      switch (sortingType) {
        case 'ascending':
          setLocations((previous) => sortBy(previous, [valueOf]));
          break;
        case 'descending':
          setLocations((previous) => reverse(sortBy(previous, [valueOf])));
          break;
        case 'unsorted':
        default:
          setLocations(initialLocationsRef.current);
          break;
      }
    },
    [],
  );

  const columns = useMemo(() => {
    const cols = [
      {
        DataCell: (data) => (
          <span>{data.rowData.attributes.srm_account_id}</span>
        ),
        sortBy: onSort('attributes.srm_account_id'),
        title: 'SRM ACCOUNT ID',
        name: 'srm_account_id',
      },
      {
        DataCell: (data) =>
          authorizeOn() ? (
            <Link
              to={`/location_settings/${
                data.rowData.attributes.slug
                  ? data.rowData.attributes.slug
                  : data.rowData.id
              }`}
            >
              {data.rowData.attributes.name}
            </Link>
          ) : (
            <span>{data.rowData.attributes.name}</span>
          ),
        sortBy: onSort('attributes.name'),
        title: 'NAME',
        name: 'name',
      },
      {
        DataCell: (data) => (
          <span>
            {data.rowData.attributes.city}, {data.rowData.attributes.state}
          </span>
        ),
        sortBy: onSort('attributes.city'),
        title: 'CITY/STATE',
        name: 'city',
      },
      {
        DataCell: (data) => (
          <span>{data.rowData.attributes.oems?.join(',').toUpperCase()}</span>
        ),
        sortBy: onSort('attributes.oems'),
        title: 'OEM',
        name: 'oem',
      },
      {
        DataCell: (data) => <span>{data.rowData.attributes.account_type}</span>,
        sortBy: onSort('attributes.account_type'),
        title: 'TYPE',
        name: 'account_type',
      },
      {
        DataCell: (data) => (
          <Tag
            key={data.rowData.attributes.id}
            text={data.rowData.attributes.enabled ? 'Enabled' : 'Disabled'}
            color={
              data.rowData.attributes.enabled ? 'success' : 'licoriceMousse'
            }
          />
        ),
        sortBy: onSort('attributes.enabled'),
        title: 'STATUS',
        name: 'status',
      },
    ];

    // Only meaningful when this integration is in a Shared Setting — otherwise
    // there is no lifecycle sync for a location to be in or out of.
    if (inSharedSetting) {
      cols.push({
        DataCell: (data) => {
          const excluded =
            data.rowData.attributes.ignore_shared_setting_actions === true;
          return (
            <Tooltip
              placement="left"
              target={
                <div>
                  <Tag
                    text={excluded ? t`Excluded` : t`Included`}
                    color={excluded ? 'warning' : 'information'}
                  />
                </div>
              }
            >
              {excluded
                ? t`Enable, disable and remove are not synced with the other integrations in this location's Shared Setting.`
                : t`Enabling, disabling or removing this location also applies to the other integrations in its Shared Setting.`}
            </Tooltip>
          );
        },
        sortBy: onSort('attributes.ignore_shared_setting_actions'),
        title: t`SYNC STATUS`,
        name: 'sync_status',
      });
    }

    return cols;
  }, [onSort, inSharedSetting]);

  const handleChangeOem = (e) => {
    setFilterOem(e);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleChangeDealerType = (e) => {
    setFilterDealerType(e);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleChangeFilterSearch = useCallback(
    debounce((value) => {
      setFilterSearch(value);
      setCurrentPage(1);
    }, 500),
    [],
  );

  const onInputChange = (e) => {
    const { value } = e.target;
    setInputValue(value); // For immediate UI feedback
    handleChangeFilterSearch(value); // Debounced backend search
  };

  const handleExportLocations = async () => {
    setVisibleExportModal(true);
  };

  return (
    <Flex flexDirection="column" name="locationResult" flexGrow={1}>
      <Flex name="searchBar">
        <Flex
          marginTop={2}
          marginLeft={2}
          marginRight={2}
          name="topBar"
          flexGrow={1}
        >
          <Flex name="searchBox">
            <Flex>
              <H3 marginRight={2} marginTop={0.2}>
                <Trans>Locations</Trans>
              </H3>
              <Separator />
            </Flex>
            <Flex flexDirection="column">
              <FilterTextField
                width={32}
                icon={iconix.Search}
                label={t`searchInput`}
                hideLabel
                value={inputValue}
                onChange={onInputChange}
              />
              <HelpInputMessage alignItems="center">
                <iconix.ExclamationCircleF />
                <HelpInputText shade={1}>
                  <Trans>Search by SRM Account ID, name or city</Trans>
                </HelpInputText>
              </HelpInputMessage>
            </Flex>
          </Flex>
          <Flex
            flexDirection="row"
            name="filters"
            marginLeft={1.9}
            marginBottom={5}
          >
            <Flex>
              <Label shade={1}>{t`OEM`}:</Label>
              <SelectMenu
                multiple
                label=""
                name="select-oem"
                options={oemOptions}
                value={filterOem}
                onChange={handleChangeOem}
                zIndex={999}
              />
            </Flex>

            <Flex marginLeft={1}>
              <Label shade={1}>{t`Dealer Type`}:</Label>
              <SelectMenu
                multiple
                label=""
                name="select-dealer-type"
                options={dealerTypeOptions}
                value={filterDealerType}
                onChange={handleChangeDealerType}
                zIndex={999}
              />
            </Flex>
          </Flex>
          {authorizeOn() ? (
            <Flex justifyContent="flex-end" flexGrow={1}>
              <Button
                icon={iconix.PlusCircle}
                text={t`ADD LOCATION`}
                kind="secondary"
                onClick={() => setVisibleModal(true)}
              />
              <Flex marginX={1}>
                <Tooltip
                  target={
                    <div>
                      <Button
                        aria-label={t`Export Locations`}
                        icon={iconix.Download}
                        kind="secondary"
                        onClick={handleExportLocations}
                      />
                    </div>
                  }
                  placement="top"
                >
                  Export all configured locations settings
                </Tooltip>
              </Flex>
            </Flex>
          ) : (
            ''
          )}
        </Flex>
      </Flex>
      <Flex name="searchResult">
        <WrapperWithLoading loading={loading}>
          {isEmpty(locations) ? (
            <NoLocationsFound
              showsConfiguredLocations
              filterSearch={filterSearch}
              filterOem={filterOem}
              filterDealerType={filterDealerType}
            />
          ) : (
            <Flex flexGrow={1} flexDirection="column">
              <div id="LocationsTable">
                <Table
                  columns={columns}
                  data={locations}
                  kind="secondary"
                  getRowKey={({ id }) => id}
                  footer={() => (
                    <Flex
                      flex={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Flex>
                        <P>
                          {(currentPage - 1) * itemsPerPage + 1} -{' '}
                          {Math.min(currentPage * itemsPerPage, totalLocations)}{' '}
                          of {totalLocations} Locations
                        </P>
                      </Flex>
                      <Pagination
                        totalPages={totalPages}
                        onPageChange={pageChangeHandler}
                        activePage={currentPage}
                      />
                    </Flex>
                  )}
                />
              </div>
            </Flex>
          )}
        </WrapperWithLoading>
      </Flex>
    </Flex>
  );
}
LocationsResult.propTypes = {
  integrationId: PropTypes.string.isRequired,
  setVisibleModal: PropTypes.func.isRequired,
  setVisibleExportModal: PropTypes.func.isRequired,
  inSharedSetting: PropTypes.bool,
};
